import { ArrowLeftIcon, ArrowRightIcon, GlobeIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { BeatsEditor } from '@/components/discover/BeatsEditor';
import { DiscoverPill } from '@/components/discover/DiscoverPill';
import {
  SUGGESTED_BRANDS_BY_VERTICAL,
  getTaxonomyFor,
} from '@/components/discover/preferences-taxonomy';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { WebsiteScanLineIcon } from '@/components/ui/illustrations';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useBusinessContext } from '@/hooks/useBusinessContext';
import { businessTypeToRelationshipKind } from '@/hooks/useCompetitorRelationshipKind';
import { useDiscoverPreferences } from '@/hooks/useDiscoverPreferences';
import type { BrandResearcherContext } from '@/lib/agent-links/startBrandResearcher';
import { validateWebsiteEntry } from '@/lib/brand/website-entry';
import { ITEM_META, MOBILE_PILL_HEIGHT, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { showErrorToast } from '@/utils/toast';

interface AddBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Launches the Brand researcher agent, seeded with the website + step-2 context. */
  onStart: (websiteUrl?: string, context?: BrandResearcherContext) => void;
  /** How many brands this account already has — changes the pitch, not the flow. */
  existingCount: number;
  /**
   * Franchise/enterprise: the new row is a location inheriting a parent
   * brand kit, not an independent workspace. Same create flow as an ordinary
   * workspace, but `handleCreate` below resolves `parent_brand_kit_id` from
   * `activeKit` (this account's currently active workspace at the moment
   * "Add a location" is clicked) and passes it into `createKit`. Neither
   * call site (`app-sidebar.tsx`, `WorkspacesSettings.tsx`) offers an
   * explicit parent picker — both just gate this boolean on tier — so the
   * active workspace IS the only signal of intended parent. Deliberately
   * client-side, not a DB default/trigger: which existing kit becomes a
   * parent is a UI intent decision (which button the user clicked, which
   * workspace they were standing in), not a data invariant that must hold
   * regardless of caller — `account_id` right next to it on the same insert
   * is resolved the same way for the same reason.
   */
  isLocation?: boolean;
}

/**
 * "Add a brand" — a two-step flow. Step 1 names it and creates it (a name,
 * optionally a website). Step 2 is "Make Discover yours" — beats, subjects and
 * one tracked-brands field, reused verbatim from `FirstVisitModal` rather
 * than forked. Both close together as ONE setup, not two things a user
 * discovers separately.
 *
 * WHY A SECOND STEP, FOUNDER 2026-08-23. Step 2's answers are not a separate
 * feature bolted on — they are CONTEXT for the thing step 1 already launches.
 * Who an account follows, competes with, and covers becomes part of the same
 * prompt handed to the Brand researcher (`buildBrandResearcherUrl`'s
 * `context` param), and seeds the ad library (`competitors` table) and the
 * Discover feed before the user ever opens either surface. Landing with
 * things already primed beats landing empty and being asked the same
 * questions again inside chat.
 *
 * ONE FIELD, NOT TWO, FOUNDER 2026-09-09. This used to be two parallel chip
 * inputs here — "Followed brands" (wrote `discover_user_preferences.
 * followed_brands`, an array the AI agent's plan-grounding never reads) and
 * "Competitors" (wrote the real `competitors` table via `/api/competitors/
 * track`, which the agent DOES read) — both captioned "we'll track these in
 * your ad library," which was only ever true of the second one. A founder
 * typing real competitor names into "Followed brands" (the more natural-
 * sounding label) got a panel that rendered correctly on Discover and was
 * invisible everywhere else — Settings, the Desk rail, and the AI agent all
 * read `competitors`, which stayed empty. One input now writes `competitors`
 * with a `relationship_kind` resolved from business type (see
 * `businessTypeToRelationshipKind`), and `BrandResearcherContext` below
 * still routes it into the right prompt slot (`followedBrands` vs
 * `competitors`) so the Brand researcher keeps the distinction it always had
 * — the user just doesn't have to pick which box to type it into.
 *
 * WHAT THIS REPLACES, TWICE OVER (kept from the single-step history — still
 * true of step 1).
 *
 * First it was `+ Add brand kit` calling `createKit()`, which inserted a row
 * literally named `'New brand kit'`, silently made it active, and left the user
 * staring at an empty kit. On the test account that produced two rows both
 * called "New brand kit", indistinguishable in the switcher — and the image
 * pipeline once printed that string onto a product because a label existed.
 *
 * Then it became a pitch with no form at all: a paragraph about the Brand
 * researcher and a button that dropped you into a chat conversation. That
 * removed the placeholder problem by removing creation entirely — and the
 * agent had no tool to create a kit either, so "Add another brand" quietly
 * edited the FIRST brand's voice instead. The founder's read was blunt and
 * correct: "this is confusing."
 *
 * The actual lesson from the first version was never "forbid forms". It was
 * **refuse to invent a name.** A field the user fills cannot produce a
 * placeholder, so a two-field form is safe in a way `createKit()` never was.
 *
 * So: the brand exists the moment you name it in step 1, and you are switched
 * into it immediately — step 2 asks about a brand that already exists, not a
 * hypothetical one. Step 2 can always be skipped; nothing here blocks landing
 * on the Brand researcher with just a name and (optionally) a website.
 */
export function AddBrandDialog({
  open,
  onOpenChange,
  onStart,
  existingCount,
  isLocation = false,
}: AddBrandDialogProps): JSX.Element {
  const isFirstExtra = existingCount <= 1;
  const { createKit, switchActiveKit, renameKit, activeKit } = useBrandKits();
  // Root-normalized: if the active workspace is itself a location, the new
  // sibling links to ITS parent rather than nesting a location under a
  // location — `inherit_brand_kit` only ever walks one hop up, so a 3-level
  // chain would silently stop enforcing the true corporate's locked fields.
  const parentBrandKitId = isLocation
    ? (activeKit?.parent_brand_kit_id ?? activeKit?.id ?? undefined)
    : undefined;
  const { session } = useAuth();
  const { update: updateDiscoverPreferences } = useDiscoverPreferences();
  // Workspace-first default for the taxonomy picker: the CURRENTLY active
  // workspace's own resolved business type (kit → linked voice → the shared
  // account `company_type` only as `getBrandFacts()`'s last resort), never
  // the account classification directly — an agency account can hold
  // differently typed client workspaces (settled workspace-tenancy model,
  // 2026-08-23). The brand being created here has no data of its own yet,
  // so this is the best available default, not a bypass of the chain.
  const { businessContext } = useBusinessContext();
  const businessType = businessContext?.businessType;
  const taxonomy = React.useMemo(() => getTaxonomyFor(businessType), [businessType]);
  const relationshipKind = businessTypeToRelationshipKind(businessType);

  const [step, setStep] = React.useState<'brand' | 'discover'>('brand');
  const [name, setName] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [websiteError, setWebsiteError] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isFinishing, setIsFinishing] = React.useState(false);

  const [newKitId, setNewKitId] = React.useState<string | null>(null);
  const createdKitRef = React.useRef<{ id: string; name: string } | null>(null);
  const [normalizedWebsite, setNormalizedWebsite] = React.useState<string | undefined>(undefined);
  const [topics, setTopics] = React.useState<Set<string>>(new Set());
  const [verticals, setVerticals] = React.useState<Set<string>>(new Set());
  const [subjects, setSubjects] = React.useState<string[]>([]);
  const [followedBrands, setFollowedBrands] = React.useState<string[]>([]);

  // Reset per opening. A dialog that reopens holding the last brand's name is
  // a way to create a duplicate by accident.
  React.useEffect(() => {
    if (open) {
      setStep('brand');
      setName('');
      setWebsite('');
      setWebsiteError(null);
      setIsCreating(false);
      setIsFinishing(false);
      setNewKitId(null);
      createdKitRef.current = null;
      setNormalizedWebsite(undefined);
      setTopics(new Set());
      setVerticals(new Set());
      setSubjects([]);
      setFollowedBrands([]);
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedWebsite = website.trim();
  // The website is optional, so blank is valid — but a filled-in one that the
  // scanner could never fetch is not. Anything invalid must fail HERE, in a
  // field the user is still looking at, rather than 15 seconds later as a
  // "we could not reach it" from the Brand researcher.
  const websiteEntry = trimmedWebsite ? validateWebsiteEntry(trimmedWebsite) : null;
  const canSubmit = trimmedName.length > 0 && !isCreating && websiteEntry?.error == null;

  const suggestedBrands = React.useMemo(() => {
    const byVertical: string[][] = [];
    for (const v of verticals) {
      const brands = SUGGESTED_BRANDS_BY_VERTICAL[v] ?? [];
      if (brands.length) byVertical.push([...brands]);
    }
    const seen = new Set<string>();
    const out: string[] = [];
    let pulled = true;
    while (pulled) {
      pulled = false;
      for (const list of byVertical) {
        const next = list.shift();
        if (!next) continue;
        pulled = true;
        const key = next.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(next);
      }
    }
    return out;
  }, [verticals]);

  const toggleTopic = (value: string) =>
    setTopics((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const toggleVertical = (value: string) =>
    setVerticals((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    // A location with no resolvable parent is indistinguishable from an
    // unrelated workspace — the exact defect this flow used to have. Refuse
    // rather than silently create an orphan (activeKit can be momentarily
    // unresolved on a fast click right after load).
    if (isLocation && !parentBrandKitId) {
      showErrorToast(
        'Could not add the location',
        'No active workspace to attach it to. Switch into the parent workspace and try again.',
      );
      return;
    }

    setIsCreating(true);
    try {
      const existing = createdKitRef.current;
      let kitId: string | undefined;
      if (existing) {
        if (existing.name !== trimmedName) {
          await renameKit(existing.id, trimmedName);
          createdKitRef.current = { id: existing.id, name: trimmedName };
        }
        kitId = existing.id;
      } else {
        kitId = parentBrandKitId
          ? await createKit(trimmedName, parentBrandKitId)
          : await createKit(trimmedName);
        createdKitRef.current = kitId ? { id: kitId, name: trimmedName } : null;
      }
      if (kitId) {
        // Switch into it. Creating a brand you are not in is a half-finished
        // action — every product that has workspaces moves you on create.
        //
        // Awaited and gated, not fire-and-forget: the DB can accept the
        // create and still reject the switch (a real, live-reproduced case —
        // see enforce_user_profiles_active_kit_ownership()). Advancing to
        // step 2 regardless would tell the user they're now acting in a
        // workspace they never actually switched into, and every follow-up
        // write in step 2 (Discover preferences, competitor tracking) would
        // land against the WRONG workspace's context.
        try {
          await switchActiveKit(kitId);
        } catch (switchErr) {
          const message = switchErr instanceof Error ? switchErr.message : 'Please try again.';
          showErrorToast(
            `"${trimmedName}" was created, but Mora could not switch you into it`,
            message,
          );
          setIsCreating(false);
          return;
        }
        setNewKitId(kitId);
      }
      setNormalizedWebsite(websiteEntry?.normalizedUrl);
      setIsCreating(false);
      setStep('discover');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.';
      showErrorToast('Could not add the brand', message);
      setIsCreating(false);
    }
  }

  // Lightweight, unverified rows — same endpoint FirstVisitModal's brands
  // section uses (POST /api/competitors/add, not the YouTube-verified
  // /api/competitors/track), so a name with no resolvable YouTube channel
  // still lands instead of silently failing.
  async function addCompetitors(brandKitId: string, names: string[]) {
    const token = session?.access_token;
    if (!token || names.length === 0) return;
    await Promise.allSettled(
      names.map((name) =>
        fetch('/api/competitors/add', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name, brandKitId, relationshipKind }),
        }),
      ),
    );
  }

  async function finishSetup(includeDiscoverAnswers: boolean) {
    setIsFinishing(true);
    try {
      if (includeDiscoverAnswers && newKitId) {
        updateDiscoverPreferences({
          topics: Array.from(topics),
          verticals: Array.from(verticals),
          beats: subjects,
          dismissed_topics_panel: true,
        });
        await addCompetitors(newKitId, followedBrands);
      }
      onOpenChange(false);
      // BrandResearcherContext keeps the distinction relationship_kind
      // encodes — "brands they follow for inspiration" reads differently in
      // the researcher's prompt than "their competitors" — the single input
      // above just spares the user from having to pick which box to type
      // a name into.
      onStart(
        normalizedWebsite,
        includeDiscoverAnswers
          ? relationshipKind === 'inspiration'
            ? { followedBrands, subjects }
            : { competitors: followedBrands, subjects }
          : undefined,
      );
    } finally {
      setIsFinishing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent responsive hideCloseButton size="md" className="gap-0 overflow-hidden p-0">
        {/* One contained coloured region, per the design system's licensed
            exception — colour lives in a cover band, never as a wash under
            body text. `WebsiteScanLineIcon` is an existing governed line
            illustration, and it still earns its place: the website field
            below (step 1) is the thing it depicts. */}
        <div className="relative isolate overflow-hidden bg-sidebar px-6 py-7">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-6 -top-8 size-36 rounded-full bg-primary/20 blur-3xl"
          />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="mb-1.5 flex items-center gap-1.5" aria-hidden="true">
                <span
                  className={cn(
                    'h-1 w-5 rounded-full',
                    step === 'brand' ? 'bg-foreground' : 'bg-foreground/30',
                  )}
                />
                <span
                  className={cn(
                    'h-1 w-5 rounded-full',
                    step === 'discover' ? 'bg-foreground' : 'bg-foreground/30',
                  )}
                />
              </div>
              <DialogTitle>
                {step === 'brand'
                  ? isFirstExtra
                    ? isLocation
                      ? 'Add a location'
                      : 'Add a workspace'
                    : isLocation
                      ? 'Add another location'
                      : 'Add another workspace'
                  : 'Make Discover yours'}
              </DialogTitle>
              <p className="mt-2 max-w-[38ch] text-detail leading-relaxed text-muted-foreground">
                {step === 'brand'
                  ? isLocation
                    ? 'Its own voice, logo, colours and board — inherits what corporate locks, switch to yours from the header.'
                    : 'Its own voice, logo, colours and board — nothing shared with the others. Switch between workspaces any time from the header.'
                  : `${trimmedName || 'This workspace'} is set up. Tell us who you follow and cover — it primes the Brand researcher, Discover and your ad library.`}
              </p>
            </div>
            <WebsiteScanLineIcon
              aria-hidden="true"
              className="h-20 w-auto shrink-0 self-center sm:translate-x-3"
            />
          </div>
        </div>

        {step === 'brand' ? (
          <form onSubmit={handleCreate} className="px-6 py-5">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="add-brand-name">
                  {isLocation ? 'Location name' : 'Workspace name'}
                </Label>
                {/* `size="sm"` is h-11 md:h-9 — the SAME geometry `InputGroup`
                    hardcodes. Without it this field renders 40px against the
                    website group's 36px, and two fields stacked 4px apart in one
                    form read as a mistake. Measured, not eyeballed. */}
                <Input
                  size="sm"
                  id="add-brand-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Northbloom Coffee"
                  autoFocus
                  autoComplete="off"
                  disabled={isCreating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-brand-website">
                  Website <span className="text-muted-foreground">(optional)</span>
                </Label>
                {/* Same shape as onboarding's "Add your website" step
                    (`onboarding-v2/steps/BrandStep.tsx`) and Settings → Brand
                    (`IntegratedBrandKit.tsx`) — an `InputGroup` with a leading
                    globe glyph read as part of one field (no bg/border on the
                    addon — the group owns the only border), blur-normalisation,
                    and an inline error. */}
                <InputGroup
                  data-invalid={websiteError ? true : undefined}
                  data-disabled={isCreating ? true : undefined}
                >
                  <InputGroupAddon align="inline-start" className="pl-2.5 text-muted-foreground/70">
                    <GlobeIcon aria-hidden className="size-4" weight="regular" />
                  </InputGroupAddon>
                  <InputGroupInput
                    id="add-brand-website"
                    value={website}
                    onChange={(e) => {
                      setWebsite(e.target.value);
                      if (websiteError) setWebsiteError(null);
                    }}
                    onBlur={() => setWebsiteError(websiteEntry?.error ?? null)}
                    placeholder="northbloom.com"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    autoCapitalize="none"
                    spellCheck={false}
                    disabled={isCreating}
                    aria-invalid={websiteError ? true : undefined}
                    aria-describedby={
                      websiteError
                        ? 'add-brand-website-error add-brand-website-help'
                        : 'add-brand-website-help'
                    }
                    className="pl-2"
                  />
                </InputGroup>
                {websiteError ? (
                  <p
                    id="add-brand-website-error"
                    role="alert"
                    className="text-detail text-destructive"
                  >
                    {websiteError}
                  </p>
                ) : null}
                <p id="add-brand-website-help" className={cn(ITEM_META, 'leading-relaxed')}>
                  Mora reads the page for the logo, real colours, voice and content pillars, and
                  shows you everything it found before saving any of it.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={MOBILE_PILL_HEIGHT}
                onClick={() => onOpenChange(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="neutral"
                size="sm"
                className={MOBILE_PILL_HEIGHT}
                disabled={!canSubmit}
              >
                {isCreating ? <Spinner size="sm" /> : null}
                Continue
                {!isCreating ? <ArrowRightIcon weight="regular" className="size-3.5" /> : null}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col">
            <div className="max-h-[55vh] space-y-6 overflow-y-auto px-6 py-5">
              <section>
                <p className={cn('mb-3', SECTION_LABEL)}>What you cover</p>
                <div className="flex flex-wrap gap-2">
                  {taxonomy.topics.map((t) => (
                    <DiscoverPill
                      key={`t-${t.value}`}
                      label={t.label}
                      icon={t.icon}
                      selected={topics.has(t.value)}
                      onToggle={() => toggleTopic(t.value)}
                    />
                  ))}
                  {taxonomy.verticals.map((v) => (
                    <DiscoverPill
                      key={`v-${v.value}`}
                      label={v.label}
                      icon={v.icon}
                      selected={verticals.has(v.value)}
                      onToggle={() => toggleVertical(v.value)}
                    />
                  ))}
                </div>
              </section>

              <section>
                <p className={cn('mb-1', SECTION_LABEL)}>Subjects you follow</p>
                <p className={cn('mb-3', ITEM_META)}>
                  Stories matching these go to the top of your feed.
                </p>
                <BeatsEditor
                  beats={subjects}
                  onChange={setSubjects}
                  suggestionKey={Array.from(verticals)[0] ?? null}
                />
              </section>

              <section>
                <p className={cn('mb-1', SECTION_LABEL)}>{taxonomy.brandsTitle}</p>
                <p className={cn('mb-3', ITEM_META)}>{taxonomy.brandsDescription}</p>
                <BeatsEditor
                  beats={followedBrands}
                  onChange={setFollowedBrands}
                  suggestions={suggestedBrands}
                  placeholder="Type a brand and press Enter"
                  emptyHint="Pick a vertical above to see brand suggestions, or type your own."
                />
              </section>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border/40 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={MOBILE_PILL_HEIGHT}
                onClick={() => setStep('brand')}
                disabled={isFinishing}
              >
                <ArrowLeftIcon weight="regular" className="size-3.5" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void finishSetup(false)}
                  disabled={isFinishing}
                  className="text-detail text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Skip for now
                </button>
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  className={MOBILE_PILL_HEIGHT}
                  onClick={() => void finishSetup(true)}
                  disabled={isFinishing}
                >
                  {isFinishing ? <Spinner size="sm" /> : null}
                  Finish setup
                  {!isFinishing ? <ArrowRightIcon weight="regular" className="size-3.5" /> : null}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AddBrandDialog;
